import React, { useState } from "react";
import styled from "styled-components";
import { useDroppable } from "@dnd-kit/core";
import { colors } from "utils";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { AssignmentGroupCard, CopyIcon } from "./AssignmentGroupCard";
import { AddGroupCodeTile } from "./AddGroupCodeTile";
import { OneListLessonCard } from "./OneListLessonCard";

export const SESSION_LENGTH_OPTIONS = [
  { value: 12.5, label: "10-15 Minutes" },
  { value: 17.5, label: "15-20 Minutes" },
  { value: 22.5, label: "20-25 Minutes" },
  { value: 27.5, label: "25-30 Minutes" },
  { value: 32.5, label: "30-35 Minutes" },
];

export const sessionLengthLabel = (mins) => {
  const exact = SESSION_LENGTH_OPTIONS.find((opt) => opt.value === mins);
  if (exact) return exact.label;
  const bucket = SESSION_LENGTH_OPTIONS.find((opt) => mins <= opt.value + 2.5);
  return bucket ? bucket.label : `${mins} Minutes`;
};

const formatLastUpdated = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US");
};

const compareCode = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

// Card order within a bundle: all Core, then Prereqs, then Extensions, then Other
// (unknown tracks last). Tolerates the singular/plural track forms. Cards within a
// track keep their code order.
const TRACK_ORDER = { core: 0, prerequisite: 1, prerequisites: 1, extension: 2, extensions: 2, other: 3 };
const trackRank = (t) => (t in TRACK_ORDER ? TRACK_ORDER[t] : 99);

const PieIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={colors.nightlyWoods500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </svg>
);

const InfoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.nightlyWoods500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// Trash-2 (Figma 5127-62371): archiving a lesson clears its group codes.
const ArchiveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 6H21M19 6V20C19 21 18 22 17 22H7C6 22 5 21 5 20V6M8 6V4C8 3 9 2 10 2H14C15 2 16 3 16 4V6M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Header action row: Edit · Copy Lesson · Archive · collapse toggle.
const HeaderActions = styled.div`
  align-items: center;
  align-self: center;
  display: flex;
  flex-shrink: 0;
  gap: 24px;
`;

// Focused edit mode puts the primary action in the header (Figma 4884-52207); the
// bottom save bar is not rendered there. Cancel rides alongside — the design shows
// only Save, but it is the sole path back to the server state.
const HeaderSave = styled.button`
  background: ${colors.blue500};
  border: none;
  border-radius: 8px;
  color: ${colors.milkGlass500};
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 24px;

  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const IconAction = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.blue500};
  cursor: pointer;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  padding: 0;
  width: 40px;

  &:hover { background: ${colors.blue50}; }
`;

// Same pencil shape as the Edit Lesson CTA (EditLessonIcon), with currentColor so
// the transparent "Change Lesson Focus" link keeps its blue stroke.
const EditFocusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5L19 9M21.1739 6.81189C21.7026 6.28332 21.9997 5.56636 21.9998 4.81875C21.9999 4.07113 21.703 3.3541 21.1744 2.82539C20.6459 2.29668 19.9289 1.99961 19.1813 1.99951C18.4337 1.99942 17.7166 2.29632 17.1879 2.82489L3.84193 16.1739C3.60975 16.4054 3.43805 16.6904 3.34193 17.0039L2.02093 21.3559C1.99509 21.4424 1.99314 21.5342 2.01529 21.6217C2.03743 21.7092 2.08285 21.7891 2.14673 21.8529C2.21061 21.9167 2.29055 21.962 2.37809 21.984C2.46563 22.006 2.55749 22.0039 2.64393 21.9779L6.99693 20.6579C7.3101 20.5626 7.59511 20.392 7.82693 20.1609L21.1739 6.81189Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Pencil icon for the Edit Lesson CTA. currentColor so it follows the button's
// text color (now the secondary/outlined purple).
const EditLessonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5L19 9M21.1739 6.81189C21.7026 6.28332 21.9997 5.56636 21.9998 4.81875C21.9999 4.07113 21.703 3.3541 21.1744 2.82539C20.6459 2.29668 19.9289 1.99961 19.1813 1.99951C18.4337 1.99942 17.7166 2.29632 17.1879 2.82489L3.84193 16.1739C3.60975 16.4054 3.43805 16.6904 3.34193 17.0039L2.02093 21.3559C1.99509 21.4424 1.99314 21.5342 2.01529 21.6217C2.03743 21.7092 2.08285 21.7891 2.14673 21.8529C2.21061 21.9167 2.29055 21.962 2.37809 21.984C2.46563 22.006 2.55749 22.0039 2.64393 21.9779L6.99693 20.6579C7.3101 20.5626 7.59511 20.392 7.82693 20.1609L21.1739 6.81189Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Text-only Edit CTA in the lesson header (Figma 5127-62371).
const EditLessonButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.blue700};
  cursor: pointer;
  display: inline-flex;
  font-family: Inter, sans-serif;
  font-size: 18px;
  gap: 8px;
  height: 48px;
  justify-content: center;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 4px;
  text-align: center;
  &:hover { text-decoration: underline; }
`;

// Blue rounded-square collapse/expand toggle (matches Figma 3456-28720): corner
// arrows point inward when expanded (collapse) and outward when collapsed (expand).
const CollapseToggleIcon = ({ expanded }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {expanded ? (
      <>
        <path d="M8 3v3a2 2 0 0 1-2 2H3" />
        <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
        <path d="M3 16h3a2 2 0 0 1 2 2v3" />
        <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
      </>
    ) : (
      <>
        <path d="M8 3H5a2 2 0 0 0-2 2v3" />
        <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
        <path d="M3 16v3a2 2 0 0 0 2 2h3" />
        <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
      </>
    )}
  </svg>
);

export const AssignmentAccordion = ({
  bundle,
  draftAssignments,
  unassignedCodes,
  sessionLength,
  sessionLengthEditable = true,
  // Standard bundles rendered in the lesson list carry no session length in the
  // unified read, so the row + summary entry are hidden for them. Defaults true
  // to leave the lesson and differentiation flows unchanged.
  showSessionLength = true,
  // Lesson bundles pass their "GRADE X, UNIT Y, LESSON Z" label as the header
  // eyebrow (replacing "Lesson for CORE STANDARD - {code}"). Defaults to the
  // current standard/differentiation header.
  eyebrow,
  // When true, the header summary (Session Length | N Groups) stays visible in
  // edit mode. Standard flow leaves this false and relies on its "Last updated"
  // line; lessons have no such line, so the summary would otherwise vanish.
  keepMetaWhileEditing = false,
  isEditing,
  onEnterEdit,
  // Focused edit mode only: when the caller hands these over, Save / Cancel render
  // in the header instead of the fixed bottom bar.
  onSave,
  saving = false,
  onSessionLengthChange,
  onArchive,
  onCopyLesson,
  onChangeLessonFocus,
  onAddCode,
  onTrashCode,
  onEditCardStandard,
  onViewQuestions,
  onDuplicateCard,
  // "oneList" draws Figma's card for Manage Groups as one list; its expansion
  // then belongs to the list (`expanded`, `onToggleExpanded`).
  appearance = "default",
  expanded: listExpanded,
  onToggleExpanded,
  editButtonId,
}) => {
  const [ownExpanded, setExpanded] = useState(true);
  const expanded = appearance === "oneList" ? listExpanded : ownExpanded;
  const { primary_standard_code, primary_standard_name, last_updated_at } = bundle;
  const lastUpdated = formatLastUpdated(last_updated_at);

  // The whole lesson card is a drop target: dropping an unassigned code anywhere
  // inside it opens Edit Group Focus for this bundle.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `lesson:${bundle.assignment_id}`,
    data: { kind: "bundle", assignmentId: bundle.assignment_id },
  });

  const cards = [...draftAssignments].sort(
    (a, b) => trackRank(a.track_type) - trackRank(b.track_type) || compareCode(a.group_code, b.group_code),
  );

  // Read-only header summary: "Last updated | Session Length | N Groups".
  const showSummary = !isEditing || keepMetaWhileEditing;
  const metaParts = [
    lastUpdated && `Last updated: ${lastUpdated}`,
    showSummary && showSessionLength && `Session Length: ${sessionLengthLabel(sessionLength)}`,
    showSummary && `${cards.length} ${cards.length === 1 ? "Group" : "Groups"}`,
  ].filter(Boolean);
  // Edit mode swaps that line for the session-length control, inline in the header
  // (Figma 4988-92387). Out of edit mode the header reads exactly as before.
  const showSessionLengthControl = isEditing && showSessionLength;

  // Built once, shared by both appearances: the one-list card keeps today's
  // edit controls and body.
  const changeLessonFocusButton = (
    <button
      type="button"
      onClick={onChangeLessonFocus}
      style={{
        alignItems: "center",
        background: "transparent",
        border: "none",
        color: colors.blue700,
        cursor: "pointer",
        display: "inline-flex",
        fontSize: 14,
        gap: 4,
        lineHeight: "20px",
        padding: "0 8px",
      }}
    >
      <EditFocusIcon />
      Change Lesson Focus
    </button>
  );
  const sessionLengthControlRow = (
    <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 16 }}>
      {lastUpdated && (
        <span style={{ color: colors.nightlyWoods500, fontSize: 14, lineHeight: "20px" }}>
          Last updated: {lastUpdated}
        </span>
      )}
      <div style={{ alignItems: "center", display: "flex", gap: 4 }}>
        <InfoIcon />
        <span style={{ color: colors.nightlyWoods500, fontSize: 14, fontWeight: 700, lineHeight: "20px" }}>
          Session Length:
        </span>
      </div>
      <div style={{ width: 175 }}>
        {sessionLengthEditable ? (
          <SelectDropdown
            ariaLabel="Session Length"
            value={sessionLength}
            displayText={sessionLengthLabel(sessionLength)}
            options={SESSION_LENGTH_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            onChange={onSessionLengthChange}
          />
        ) : (
          <span style={{ color: colors.nightlyWoods500, fontSize: 14, lineHeight: "20px" }}>
            {sessionLengthLabel(sessionLength)}
          </span>
        )}
      </div>
    </div>
  );
  const saveButton = (
    <HeaderSave type="button" onClick={onSave} disabled={saving}>
      {saving ? "Saving…" : "Save"}
    </HeaderSave>
  );
  // Cards grid — carries the header separator the session-length row used to.
  const cardsPanel = (
    <div style={{ backgroundColor: "#F9FAFD", borderTop: "1px solid #ACAFAD", padding: 16 }}>
      <div style={{
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
      }}>
        {cards.map((a) => (
          <AssignmentGroupCard
            key={a.group_code}
            assignmentId={bundle.assignment_id}
            assignment={a}
            bundle={bundle}
            isEditing={isEditing}
            onTrash={() => onTrashCode(a.group_code)}
            onEditStandard={onEditCardStandard ? () => onEditCardStandard(a.group_code) : undefined}
            onViewQuestions={onViewQuestions ? () => onViewQuestions(a) : undefined}
            onDuplicate={onDuplicateCard ? () => onDuplicateCard(a) : undefined}
            duplicateDisabled={unassignedCodes.length === 0}
          />
        ))}
        {isEditing && (
          <AddGroupCodeTile
            assignmentId={bundle.assignment_id}
            unassignedCodes={unassignedCodes}
            onAddCode={onAddCode}
          />
        )}
      </div>
    </div>
  );

  if (appearance === "oneList") {
    return (
      <OneListLessonCard
        bundle={bundle}
        title={primary_standard_name}
        eyebrow={eyebrow}
        lastUpdated={lastUpdated}
        sessionLabel={showSessionLength ? sessionLengthLabel(sessionLength) : null}
        groupCount={cards.length}
        isEditing={isEditing}
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
        isOver={isOver}
        dropRef={setDropRef}
        onEnterEdit={onEnterEdit}
        editButtonId={editButtonId}
        onCopyLesson={onCopyLesson}
        onArchive={onArchive}
        editControls={{
          changeLessonFocus: onChangeLessonFocus ? changeLessonFocusButton : null,
          sessionLengthRow: showSessionLengthControl ? sessionLengthControlRow : null,
          save: onSave ? saveButton : null,
          body: cardsPanel,
        }}
      />
    );
  }

  return (
    <div ref={setDropRef} style={{
      backgroundColor: "#F9FAFD",
      border: `1px solid ${isOver ? colors.blue500 : colors.nightlyWoods950}`,
      borderRadius: 12,
      boxShadow: isOver ? `0 0 0 2px ${colors.blue300}` : "0px 1px 2px rgba(14, 26, 49, 0.05)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* Lesson header (white); everything below sits on the #F9FAFD section bg */}
      <div style={{ backgroundColor: "white", display: "flex", gap: 8, padding: "16px 24px" }}>
        <PieIcon />
        <div style={{ display: "flex", flex: 1, flexDirection: "column", gap: 8, minWidth: 0 }}>
          <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 8 }}>
            {eyebrow ? (
              <span style={{
                color: "#BA4A21",
                fontFamily: '"ABC Marfa", sans-serif',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.5px",
                lineHeight: "18px",
                textTransform: "uppercase",
              }}>
                {eyebrow}
              </span>
            ) : (
              <>
                <span style={{ color: colors.nightlyWoods500, fontSize: 12, lineHeight: "18px" }}>Lesson for</span>
                <span style={{
                  color: "#BA4A21",
                  fontFamily: '"ABC Marfa", sans-serif',
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  lineHeight: "18px",
                  textTransform: "uppercase",
                }}>
                  Core STANDARD - {primary_standard_code}
                </span>
              </>
            )}
            {isEditing && onChangeLessonFocus && changeLessonFocusButton}
          </div>
          <span style={{ color: colors.nightlyWoods500, fontSize: 18, fontWeight: 700, letterSpacing: "0.25px", lineHeight: "26px" }}>
            {primary_standard_name}
          </span>
          {showSessionLengthControl ? sessionLengthControlRow : metaParts.length > 0 && (
            <span style={{ color: colors.nightlyWoods500, fontSize: 14, lineHeight: "20px" }}>
              {metaParts.join("  |  ")}
            </span>
          )}
        </div>
        <HeaderActions>
          {isEditing && onSave ? (
            // Save is the only action while editing, and it is never disabled: with
            // nothing changed the handler just leaves edit mode, so "Save" and
            // "Cancel" are the same gesture from the teacher's side.
            saveButton
          ) : (
          <>
          {!isEditing && onEnterEdit && (
            <EditLessonButton type="button" onClick={onEnterEdit}>
              <EditLessonIcon />
              Edit
            </EditLessonButton>
          )}
          {onCopyLesson && (
            <IconAction type="button" onClick={onCopyLesson} aria-label="Copy lesson" title="Copy Lesson">
              <CopyIcon />
            </IconAction>
          )}
          {onArchive && (
            <IconAction type="button" onClick={onArchive} aria-label="Archive lesson" title="Archive Lesson">
              <ArchiveIcon />
            </IconAction>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? "Collapse standard" : "Expand standard"}
            style={{
              alignItems: "center",
              backgroundColor: colors.blue500,
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              display: "flex",
              flexShrink: 0,
              height: 40,
              justifyContent: "center",
              padding: 0,
              width: 40,
            }}
          >
            <CollapseToggleIcon expanded={expanded} />
          </button>
          </>
          )}
        </HeaderActions>
      </div>

      {/* Collapsible body — session length + cards. Only the header shows when collapsed. */}
      <div style={{
        display: "grid",
        gridTemplateRows: expanded ? "1fr" : "0fr",
        transition: "grid-template-rows .25s ease",
      }}>
        <div style={{ minHeight: 0, overflow: "hidden" }}>
          {cardsPanel}
        </div>
      </div>
    </div>
  );
};